/**
 * Persisted touch-controls mode. Three values:
 *
 *   direct  — nipplejs floating thumbstick (default). Joystick anchors
 *             on first touch, releases on touchend. Step 3 of the touch-
 *             controls plan adds it; classic D-pad + A/B buttons hidden.
 *   command — tap a tile / NPC, character pathfinds there (Step 7).
 *             Both classic D-pad and joystick hidden.
 *   classic — the legacy on-screen D-pad + A/B chrome. Kept as a motor-
 *             accessibility fallback. Step 3 hides it by default.
 *
 * Step 4 of the plan adds the inline radio buttons in MenuScene; for now
 * Step 3 just reads the persisted setting (default 'direct') so the
 * touch-controls flag flips correctly on first boot.
 *
 * Storage failures (in-app webview, private mode) are silent — the value
 * stays in-memory only for the session and reverts to 'direct' on reload.
 * That degradation is logged as TODOs #12.6 ("Storage-blocked context
 * detection") and is intentionally NOT bumping SAVE_VERSION.
 */

export type ControlMode = 'direct' | 'command' | 'classic';

const STORAGE_KEY = 'poke-control-mode';
const VALID_MODES: readonly ControlMode[] = ['direct', 'command', 'classic'];

export function getControlMode(): ControlMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && (VALID_MODES as readonly string[]).includes(raw)) {
      return raw as ControlMode;
    }
  } catch {
    // localStorage blocked — fall through to default.
  }
  return 'direct';
}

export function setControlMode(mode: ControlMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Silent — in-memory only this session. See module docstring.
  }
}

/** Pure-function counterpart to WorldScene.applyControlModeVisibility's
 *  decision. The classic D-pad + A/B button cluster is visible iff the
 *  mode is 'classic'. Exported as a free function so the visibility
 *  rule can be unit-tested without booting Phaser (REGRESSION 2 in
 *  control-mode-default.test.ts). */
export function shouldShowClassicDpad(mode: ControlMode): boolean {
  return mode === 'classic';
}
